<section class="relative bg-slate-900 text-white overflow-hidden py-20 lg:py-32">
  <!-- Subtle Background Gradient Blur Overlay -->
  <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-600/30 via-purple-600/20 to-transparent pointer-events-none"></div>

  <div class="container mx-auto px-6 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      
      <!-- Left Hero Text Content -->
      <div class="lg:col-span-7 space-y-8">
        <!-- Top Badge -->
        <div class="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-400/30 text-blue-300 px-4 py-2 rounded-full text-sm font-medium">
          <i class="fa-solid fa-chalkboard-user text-blue-400"></i>
          <span>Empower Students • Grow Your Teaching Career</span>
        </div>

        <!-- Main Title -->
        <h1 class="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight">
          Share Your Knowledge.<br />
          <span class="bg-gradient-to-r from-blue-400 via-cyan-300 to-indigo-300 bg-clip-text text-transparent">
            Teach Thousands of Students.
          </span>
        </h1>

        <!-- Subtitle Paragraph -->
        <p class="text-lg sm:text-xl text-slate-300 max-w-2xl leading-relaxed">
          Join Learnbird to host live online classes, publish pre-recorded courses, manage study materials, build your personal tutor profile, and earn on your own terms.
        </p>

        <!-- CTAs -->
        <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
          <a
            routerLink="/auth/register"
            [queryParams]="{ role: 'instructor' }"
            class="daisy-btn daisy-btn-primary daisy-btn-lg bg-blue-600 hover:bg-blue-500 border-none text-white shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2"
          >
            <i class="fa-solid fa-rocket"></i>
            <span>Become an Instructor</span>
          </a>

          <a
            href="/teach#teach-features"
            class="daisy-btn daisy-btn-outline daisy-btn-lg text-slate-200 border-slate-700 hover:bg-slate-800 hover:text-white flex items-center justify-center gap-2"
          >
            <i class="fa-solid fa-circle-play"></i>
            <span>Explore Platform Features</span>
          </a>
        </div>

        <!-- Trust & Value Highlights -->
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-6 pt-8 border-t border-slate-800">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-blue-500/20 text-blue-400 flex items-center justify-center">
              <i class="fa-solid fa-calendar-check text-lg"></i>
            </div>
            <div>
              <p class="text-sm font-semibold text-white">Full Flexibility</p>
              <p class="text-xs text-slate-400">Set your own schedules</p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
              <i class="fa-solid fa-video text-lg"></i>
            </div>
            <div>
              <p class="text-sm font-semibold text-white">Live Zoom Integration</p>
              <p class="text-xs text-slate-400">Seamless live streaming</p>
            </div>
          </div>

          <div class="flex items-center gap-3 col-span-2 sm:col-span-1">
            <div class="w-10 h-10 rounded-xl bg-purple-500/20 text-purple-400 flex items-center justify-center">
              <i class="fa-solid fa-chart-line text-lg"></i>
            </div>
            <div>
              <p class="text-sm font-semibold text-white">Earnings Analytics</p>
              <p class="text-xs text-slate-400">Track payouts in real-time</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Visual Mockup / Hero Card -->
      <div class="lg:col-span-5">
        <div class="relative max-w-md mx-auto">
          <!-- Glassmorphism Card Frame -->
          <div class="bg-slate-800/80 backdrop-blur-xl border border-slate-700/70 rounded-3xl p-6 shadow-2xl shadow-blue-900/30">
            <!-- Simulated Header -->
            <div class="flex items-center justify-between pb-4 border-b border-slate-700/60">
              <div class="flex items-center gap-3">
                <div class="w-12 h-12 rounded-full bg-gradient-to-tr from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                  <i class="fa-solid fa-user-tie"></i>
                </div>
                <div>
                  <h3 class="font-bold text-white text-base">Tutor Hub Preview</h3>
                  <p class="text-xs text-emerald-400 flex items-center gap-1">
                    <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span> Verified Instructor
                  </p>
                </div>
              </div>
              <span class="daisy-badge daisy-badge-primary daisy-badge-outline text-xs">Active</span>
            </div>

            <!-- Live Session Preview -->
            <div class="my-6 bg-slate-900/90 rounded-2xl p-4 border border-slate-800 space-y-3">
              <div class="flex items-center justify-between text-xs text-slate-400">
                <span><i class="fa-solid fa-clock text-blue-400 mr-1"></i> Upcoming Live Class</span>
                <span class="text-blue-400 font-semibold">Today @ 7:00 PM</span>
              </div>
              <p class="font-semibold text-slate-100 text-sm">Advanced Combined Maths - Revision Session</p>
              <div class="flex items-center justify-between text-xs pt-1">
                <span class="text-slate-400"><i class="fa-solid fa-users text-indigo-400 mr-1"></i> 142 Students Enrolled</span>
                <span class="daisy-badge daisy-badge-secondary text-[10px]">Zoom Live</span>
              </div>
            </div>

            <!-- Quick Stats -->
            <div class="grid grid-cols-2 gap-3 pt-2">
              <div class="bg-slate-900/60 rounded-xl p-3 border border-slate-800 text-center">
                <p class="text-xs text-slate-400">Monthly Reach</p>
                <p class="text-xl font-bold text-blue-400">1,250+</p>
              </div>
              <div class="bg-slate-900/60 rounded-xl p-3 border border-slate-800 text-center">
                <p class="text-xs text-slate-400">Student Rating</p>
                <p class="text-xl font-bold text-amber-400">4.9 ★</p>
              </div>
            </div>
          </div>

          <!-- Decorative Floating Element -->
          <div class="absolute -bottom-6 -right-6 bg-blue-600 text-white rounded-2xl p-4 shadow-lg flex items-center gap-3 hidden sm:flex border border-blue-400/40">
            <div class="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-xl">
              <i class="fa-solid fa-graduation-cap"></i>
            </div>
            <div>
              <p class="text-xs text-blue-100">National Curriculum</p>
              <p class="text-sm font-bold">O/L, A/L & Higher Ed</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</section>
